import { path } from 'ramda'

import Segment from './segment'
import { segment1Onboarding } from './onboarding/segment1-onboarding'
import { segment1BooksyCardReader } from './booksy-card-reader/segment1-booksy-card-reader'
import {
  getAddress,
  getBusinessPrefixedPhone,
  getBusinessStatus,
  getEventDate,
  getUserPrefixedPhone,
} from '@/lib/analytics/helpers'

import { EVENTS } from '@/lib/consts.ts'
import { KYC_STATUS, USE_IDENTIFY } from '@/lib/analytics/consts.ts'

export default class Segment1 extends Segment {
  constructor (config, name) {
    super(config, name)
    Object.assign(this, segment1Onboarding)
    Object.assign(this, segment1BooksyCardReader)
  }

  [EVENTS.BUSINESS_INFO_UPDATED] ({ data }) {
    // Don't send, because event is already sent from server
    if (path(['from_server'], data)) {
      return this.sendingSkipped
    }

    const business = path(['business'], data) || {}
    const address = getAddress(business.location)
    const location = path(['location'], business) || {}

    return {
      address,
      business_name: business.name,
      city: location.city,
      event: 'Business_Info_Updated',
      event_name: EVENTS.BUSINESS_INFO_UPDATED,
      owner_email: business.owner_email,
      owner_name: business.owner_full_name,
      owner_phone: getBusinessPrefixedPhone({ business, country: data.country }),
      // postalCode is named with camel case convencion, because Segment recognizes this as special value
      postalCode: location.zipcode,
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.INVITE_PROCESS_STARTED] ({ data }) {
    return {
      event: 'Invite_Process_Started',
      event_date: getEventDate(),
      invite_process_type: path(['invite_process_type'], data),
      page_source: path(['page_source'], data),
    }
  }

  [EVENTS.MOBILE_PAYMENTS_GET_STARTED_SELECTED] () {
    return {
      business_kyc_status: KYC_STATUS.STARTED,
      event: 'KYC_Enable_Mobile_Payments_Started',
      event_name: EVENTS.MOBILE_PAYMENTS_GET_STARTED_SELECTED,
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.BOOST_CLICKED_ON_MARKETING_LIST] ({ country, business, user }) {
    const business_status = getBusinessStatus(business)
    const user_phone = getUserPrefixedPhone({ user, country })

    return {
      business_admin_status: business_status,
      business_name: business.name,
      event: 'Boost_Clicked_On_Marketing_List',
      event_name: EVENTS.BOOST_CLICKED_ON_MARKETING_LIST,
      owner_name: business.owner_full_name,
      phone: user_phone,
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.SUBSCRIPTION_CLICKED_ON_SETTINGS_LIST] ({ country, business, user }) {
    const business_status = getBusinessStatus(business)
    const user_phone = getUserPrefixedPhone({ user, country })

    return {
      business_admin_status: business_status,
      business_name: business.name,
      event: 'Subscription_Clicked_On_Settings_List',
      event_name: EVENTS.SUBSCRIPTION_CLICKED_ON_SETTINGS_LIST,
      owner_name: business.owner_full_name,
      phone: user_phone,
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.ENABLE_MOBILE_PAYMENTS_SCREEN_OPENED] ({ country, business, user }) {
    const business_status = getBusinessStatus(business)
    const user_phone = getUserPrefixedPhone({ user, country })

    return {
      business_admin_status: business_status,
      business_name: business.name,
      event: 'Enable_Mobile_Payments_Screen_Opened',
      event_name: EVENTS.ENABLE_MOBILE_PAYMENTS_SCREEN_OPENED,
      owner_name: business.owner_full_name,
      phone: user_phone,
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.SIMPLIFIED_REFERRAL_OPENED] () {
    return {
      event: 'Simplified_Referral_Opened',
      event_name: EVENTS.SIMPLIFIED_REFERRAL_OPENED,
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.SIMPLIFIED_REFERRAL_CODE_CLICKED] () {
    return {
      event: 'Simplified_Referral_Code_Clicked',
      event_name: EVENTS.SIMPLIFIED_REFERRAL_CODE_CLICKED,
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.SIMPLIFIED_REFERRAL_SHARE_CLICKED] () {
    return {
      event: 'Simplified_Referral_Share_Clicked',
      event_name: EVENTS.SIMPLIFIED_REFERRAL_SHARE_CLICKED,
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.SOCIAL_POST_SHARED] ({ business }) {
    const business_status = getBusinessStatus(business)

    return {
      business_admin_status: business_status,
      event: 'Social_Post_Shared',
      event_name: EVENTS.SOCIAL_POST_SHARED,
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.MESSAGE_BLAST_SENT] ({ business }) {
    const business_status = getBusinessStatus(business)

    return {
      business_admin_status: business_status,
      business_name: business.name,
      event: 'Message_Blast_Sent',
      event_name: EVENTS.MESSAGE_BLAST_SENT,
      owner_name: business.owner_full_name,
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.SUBSCRIPTION_SECTION_ENTERED] () {
    return {
      event: 'Subscription_Section_Entered',
      event_name: EVENTS.SUBSCRIPTION_SECTION_ENTERED,
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.SUBSCRIPTION_TRIAL_SELECT_PLAN_CLICKED] ({ data }) {
    return {
      event: 'Subscription_Trial_Select_Plan_Clicked',
      event_name: EVENTS.SUBSCRIPTION_TRIAL_SELECT_PLAN_CLICKED,
      subscription_payment_method: path(['paymentSource'], data),
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.PORTFOLIO_PHOTOS_ADDED] = ({ business }) => {
    const business_admin_status = getBusinessStatus(business)
    return {
      business_admin_status,
      event: 'Portfolio_Photos_Added',
      event_name: EVENTS.PORTFOLIO_PHOTOS_ADDED,
      [USE_IDENTIFY]: true,
    }
  }

  [EVENTS.BCR_CARD_READER_CONNECT] = ({ data }) => {
    const {
      stripe_bcr_connection_reader_type,
      stripe_bcr_software_version,
      stripe_bcr_reader_SN,
      stripe_bcr_connection_terminal_status,
      stripe_bcr_connection_terminal_type,
      stripe_bcr_connetion_ip_adress,
      stripe_bcr_failure_reason,
      stripe_bcr_failure_code,
    } = data

    return {
      event: 'BCR_Card_Reader_Connect',
      event_name: EVENTS.BCR_CARD_READER_CONNECT,
      [USE_IDENTIFY]: true,
      stripe_bcr_connection_reader_type,
      stripe_bcr_software_version,
      stripe_bcr_reader_SN,
      stripe_bcr_connection_terminal_status,
      stripe_bcr_connection_terminal_type,
      stripe_bcr_connetion_ip_adress,
      stripe_bcr_failure_reason,
      stripe_bcr_failure_code,
    }
  }

  [EVENTS.OFFLINE_MIGRATION_OFFER_ACCEPTED] = ({ business, data }) => {
    const { event_action } = data
    return {
      event: 'Subscription_Online_Action',
      business_name: business.name,
      owner_name: business.owner_full_name,
      screen_name: 'join_subscription_online_accepted',
      event_action,
    }
  }

  [EVENTS.OFFLINE_MIGRATION_ERROR] = ({ business, data }) => {
    const { event_action } = data
    return {
      event: 'Subscription_Online_Failed',
      business_name: business.name,
      owner_name: business.owner_full_name,
      screen_name: 'subscription_online_failed',
      event_action,
    }
  }

  [EVENTS.BOOKSY_PAY_ENABLED] = ({ data }) => {
    const { entrancePath } = data
    return {
      event: 'Booksy_Pay_Enabled',
      event_name: EVENTS.BOOKSY_PAY_ENABLED,
      entrance_path: entrancePath,
    }
  }
}
